import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "react-bootstrap-icons";
import { Spinner } from "react-bootstrap";
import { useGetUnassignedAgentContracts } from "../hooks/useGetUnassignedAgentContracts";
import { useGetUnassignedChargebacks } from "../hooks/useGetUnassignedChargebacks";

// Live-search dropdown for picking a commission record that is missing from a
// payout run. Both record sources return unpaged lists tailored to this picker.
export function ContractSearchDropdown({
  api,
  payoutRunId,
  value,
  onChange,
  onError,
  placeholder = "Search for commission or chargeback record…",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const { contracts, isLoading, search } = useGetUnassignedAgentContracts({
    api,
    payoutRunId,
    onError,
  });
  const {
    chargebacks,
    isLoading: isLoadingChargebacks,
    search: searchChargebacks,
  } = useGetUnassignedChargebacks({ api, payoutRunId, onError });

  const groupedContracts = contracts.reduce((groups, contract) => {
    const groupKey = contract.contractId ?? contract.contractNumber;
    const existing = groups.get(groupKey);
    if (existing) {
      existing.agentContractIds.push(contract.commissionAgentContractId);
    } else {
      groups.set(groupKey, {
        ...contract,
        recordType: "contract",
        agentContractIds: [contract.commissionAgentContractId],
      });
    }
    return groups;
  }, new Map());
  const groupedChargebacks = chargebacks.reduce((groups, chargeback) => {
    const groupKey = chargeback.contractNumber ?? chargeback.chargebackRecordId;
    const existing = groups.get(groupKey);
    if (existing) {
      existing.chargebackRecordIds.push(chargeback.chargebackRecordId);
    } else {
      groups.set(groupKey, {
        ...chargeback,
        chargebackRecordIds: [chargeback.chargebackRecordId],
      });
    }
    return groups;
  }, new Map());
  const records = [
    ...Array.from(groupedContracts.values()),
    ...Array.from(groupedChargebacks.values()),
  ];
  const commissionRecords = records.filter(
    (record) => record.recordType !== "chargeback",
  );
  const chargebackRecords = records.filter(
    (record) => record.recordType === "chargeback",
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const runSearch = useCallback(
    (term) => Promise.all([search(term), searchChargebacks(term)]),
    [search, searchChargebacks],
  );

  // Debounce query changes; also refreshes results each time the menu opens.
  useEffect(() => {
    if (!open) return;
    const handler = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(handler);
  }, [query, open, runSearch]);

  const handleToggle = () => {
    if (!open) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen((o) => !o);
    setQuery("");
  };

  const handleSelect = (contract) => {
    onChange?.(contract);
    setOpen(false);
    setQuery("");
  };

  const triggerClass = [
    "contract-search-dropdown__trigger",
    open ? "is-open" : "",
    value ? "has-value" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderOption = (record) => {
    const contractNumber =
      record.contractNumber ||
      record.chargebackType ||
      `Chargeback ${record.chargebackRecordId}`;
    const contractReference =
      record.providerNumber && record.providerNumber !== contractNumber
        ? `${contractNumber} - ${record.providerNumber}`
        : contractNumber;

    return (
      <div
        key={
          record.recordType === "chargeback"
            ? `chargeback-${record.chargebackRecordId}`
            : `contract-${record.contractId ?? record.contractNumber}`
        }
        className={`contract-search-dropdown__option${value?.contractNumber === record.contractNumber ? " is-selected" : ""}`}
        onClick={() => handleSelect(record)}
      >
        <div className="contract-search-dropdown__option-name">
          {contractReference}
        </div>
      </div>
    );
  };

  return (
    <div ref={wrapperRef} className="contract-search-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        onClick={handleToggle}
      >
        {value ? (
          <span className="contract-search-dropdown__selected">
            <span className="contract-search-dropdown__code">
              {value.contractNumber}
              {value.providerNumber &&
              value.providerNumber !== value.contractNumber
                ? ` - ${value.providerNumber}`
                : ""}
            </span>
          </span>
        ) : (
          <span>{placeholder}</span>
        )}
        <ChevronDown size={11} />
      </button>

      {open && (
        <div
          className="contract-search-dropdown__menu"
          style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
        >
          <div className="contract-search-dropdown__search">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by contract #"
            />
          </div>
          <div className="contract-search-dropdown__list">
            {isLoading || isLoadingChargebacks ? (
              <div className="contract-search-dropdown__empty">
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: "6px" }}
                />
                Searching…
              </div>
            ) : records.length === 0 ? (
              <div className="contract-search-dropdown__empty">
                No records found
              </div>
            ) : (
              <>
                {commissionRecords.length > 0 && (
                  <section className="contract-search-dropdown__section contract-search-dropdown__section--commission">
                    <h3 className="contract-search-dropdown__section-title">
                      Commissions
                    </h3>
                    {commissionRecords.map(renderOption)}
                  </section>
                )}
                {chargebackRecords.length > 0 && (
                  <section className="contract-search-dropdown__section contract-search-dropdown__section--chargeback">
                    <h3 className="contract-search-dropdown__section-title">
                      Chargebacks
                    </h3>
                    {chargebackRecords.map(renderOption)}
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
